import React, { useState, useCallback } from 'react';
import './MetadataEditor.scss';
import { apiPhotoUpdateMetadata } from '../../../actions/photoActions';

interface MetadataEditorProps {
  metadata: Record<string, any>;
  onSave?: (metadata: Record<string, any>) => void;
  readOnly?: boolean;
  title?: string;
  maxHeight?: number;
  showSearch?: boolean;
  photoId: number;
}

interface FieldProps {
  fieldKey: string;
  value: any;
  path: string;
  onUpdate: (path: string, value: any) => void;
  readOnly?: boolean;
  level?: number;
}

// Функция для конвертации байтового массива в текст
const bytesToText = (bytes: number[]): string => {
  try {
    return String.fromCharCode(...bytes);
  } catch {
    return bytes.join(', ');
  }
};

// Функция для определения, является ли значение байтовым массивом
const isByteArray = (value: any): boolean => {
  return (
    Array.isArray(value) &&
    value.length > 0 &&
    value.every(item => typeof item === 'number' && item >= 0 && item <= 255)
  );
};

// Компонент для отображения отдельного поля
const MetadataField: React.FC<FieldProps> = ({
  fieldKey,
  value,
  path,
  onUpdate,
  readOnly = false,
  level = 0,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(false); // по умолчанию все свернуто

  const handleEdit = useCallback(() => {
    if (readOnly) return;
    let stringValue = '';
    if (Array.isArray(value)) {
      stringValue = JSON.stringify(value, null, 2);
    } else if (typeof value === 'object' && value !== null) {
      stringValue = JSON.stringify(value, null, 2);
    } else {
      stringValue = String(value);
    }
    setEditValue(stringValue);
    setIsEditing(true);
  }, [value, readOnly]);

  const handleSave = useCallback(() => {
    try {
      let parsedValue: any = editValue;
      if (editValue.trim().startsWith('[') || editValue.trim().startsWith('{')) {
        parsedValue = JSON.parse(editValue);
      } else if (editValue.toLowerCase() === 'true') {
        parsedValue = true;
      } else if (editValue.toLowerCase() === 'false') {
        parsedValue = false;
      } else if (!isNaN(Number(editValue))) {
        parsedValue = Number(editValue);
      }
      onUpdate(path, parsedValue);
      setIsEditing(false);
    } catch (error) {
      alert('JSON parsing error. Check syntax.');
    }
  }, [editValue, onUpdate, path]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const renderValue = (val: any): React.ReactNode => {
    if (Array.isArray(val)) {
      if (val.length === 0) {
        return <span className="metadata-field__value-empty">[]</span>;
      }

      // Проверяем, является ли это байтовым массивом
      if (isByteArray(val)) {
        const textValue = bytesToText(val);
        return (
          <div className="metadata-field__value-byte-array">
            <div className="metadata-field__value-text">
              <strong>Текст:</strong> {textValue}
            </div>
            <div className="metadata-field__value-bytes">
              <strong>Байты:</strong> [{val.slice(0, 10).join(', ')}
              {val.length > 10 ? '...' : ''}]
            </div>
          </div>
        );
      }

      if (!isExpanded) {
        return (
          <span
            className="metadata-field__value-array"
            onClick={handleToggle}
            style={{ cursor: 'pointer' }}
          >
            [{val.length} items] ▶
          </span>
        );
      }
      return (
        <div className="metadata-field__value-array">
          <span
            className="metadata-field__value-array"
            onClick={handleToggle}
            style={{ cursor: 'pointer' }}
          >
            [{val.length} items] ▼
          </span>
          <div className="metadata-field__value-content">
            {val.map((item, idx) => (
              <div key={idx} className="metadata-field__array-item" style={{ paddingLeft: 20 }}>
                <span className="metadata-field__array-index">[{idx}]:</span>
                {typeof item === 'object' && item !== null ? (
                  <MetadataField
                    key={`${path}.${idx}`}
                    fieldKey={String(idx)}
                    value={item}
                    path={`${path}.${idx}`}
                    onUpdate={onUpdate}
                    readOnly={true}
                    level={level + 1}
                  />
                ) : (
                  <span className="metadata-field__value-simple">{String(item)}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    } else if (typeof val === 'object' && val !== null) {
      const keys = Object.keys(val);
      if (keys.length === 0) {
        return <span className="metadata-field__value-empty">{}</span>;
      }
      if (!isExpanded) {
        return (
          <span
            className="metadata-field__value-object"
            onClick={handleToggle}
            style={{ cursor: 'pointer' }}
          >
            {keys.length} fields ▶
          </span>
        );
      }
      return (
        <div className="metadata-field__value-object">
          <span
            className="metadata-field__value-object"
            onClick={handleToggle}
            style={{ cursor: 'pointer' }}
          >
            {keys.length} fields ▼
          </span>
          <div className="metadata-field__value-content">
            {keys.map(objKey => (
              <MetadataField
                key={`${path}.${objKey}`}
                fieldKey={objKey}
                value={val[objKey]}
                path={`${path}.${objKey}`}
                onUpdate={onUpdate}
                readOnly={true}
                level={level + 1}
              />
            ))}
          </div>
        </div>
      );
    } else if (typeof val === 'boolean') {
      return <span className="metadata-field__value-boolean">{val ? 'true' : 'false'}</span>;
    } else if (val === null || val === undefined) {
      return <span className="metadata-field__value-null">null</span>;
    } else {
      return <span className="metadata-field__value-simple">{String(val)}</span>;
    }
  };

  return (
    <div className="metadata-field" style={{ paddingLeft: `${level * 20}px` }}>
      <div className="metadata-field__key">{fieldKey}:</div>
      <div className="metadata-field__value">
        {isEditing ? (
          <div className="metadata-field__edit">
            <textarea
              value={editValue}
              onChange={e => setEditValue(e.target.value)}
              className="metadata-field__textarea"
              rows={Math.min(8, editValue.split('\n').length)}
            />
            <div className="metadata-field__actions">
              <button
                onClick={handleSave}
                className="metadata-field__btn metadata-field__btn--save"
              >
                Save
              </button>
              <button
                onClick={handleCancel}
                className="metadata-field__btn metadata-field__btn--cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="metadata-field__display">{renderValue(value)}</div>
            {!readOnly && (
              <button
                onClick={handleEdit}
                className="metadata-field__btn metadata-field__btn--edit"
              >
                <i className="fa fa-pencil" aria-hidden="true"></i>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Рекурсивная функция для рендеринга полей метаданных
const renderMetadataFields = (
  obj: Record<string, any>,
  path: string = '',
  onUpdate: (path: string, value: any) => void,
  readOnly: boolean = false,
  level: number = 0
): React.ReactNode[] => {
  return Object.entries(obj).map(([key, value]) => {
    const currentPath = path ? `${path}.${key}` : key;
    return (
      <MetadataField
        key={currentPath}
        fieldKey={key}
        value={value}
        path={currentPath}
        onUpdate={onUpdate}
        readOnly={readOnly}
        level={level}
      />
    );
  });
};

// Основной компонент
export const MetadataEditor: React.FC<MetadataEditorProps> = ({
  metadata,
  onSave,
  readOnly = false,
  title = '',
  maxHeight = 400,
  showSearch = true,
  photoId,
}) => {
  const [currentMetadata, setCurrentMetadata] = useState<Record<string, any>>(metadata || {});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);

  const handleUpdate = useCallback(
    (path: string, value: any) => {
      const newMetadata = { ...currentMetadata };
      const keys = path.split('.');
      let current = newMetadata;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;
      setCurrentMetadata(newMetadata);
    },
    [currentMetadata]
  );

  const handleSave = useCallback(async () => {
    if (loading) return; // Предотвращаем множественные нажатия

    setLoading(true);
    try {
      // Находим измененные поля
      const changedFields: Record<string, any> = {};

      const findChanges = (oldObj: any, newObj: any, path: string = '') => {
        for (const key in newObj) {
          const currentPath = path ? `${path}.${key}` : key;

          if (!(key in oldObj)) {
            // Новое поле
            changedFields[currentPath] = newObj[key];
          } else if (JSON.stringify(oldObj[key]) !== JSON.stringify(newObj[key])) {
            // Измененное поле
            changedFields[currentPath] = newObj[key];
          }
        }
      };

      findChanges(metadata, currentMetadata);

      // Выводим JSON с новыми и измененными полями
      console.log('JSON для API:', JSON.stringify(changedFields, null, 2));

      await apiPhotoUpdateMetadata(photoId, changedFields);
      setLoading(false);

      if (onSave) {
        await onSave(currentMetadata);
      }
    } catch (error) {
      console.error('Ошибка при сохранении:', error);
    } finally {
      setLoading(false);
    }
  }, [currentMetadata, onSave, metadata, loading]);

  const handleReset = useCallback(() => {
    setCurrentMetadata(metadata || {});
  }, [metadata]);

  // Фильтрация метаданных по поиску
  const filteredMetadata = searchTerm
    ? Object.fromEntries(
        Object.entries(currentMetadata).filter(
          ([key, value]) =>
            key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            JSON.stringify(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      )
    : currentMetadata;

  // Если нет данных для отображения
  if (!filteredMetadata || Object.keys(filteredMetadata).length === 0) {
    return (
      <div className="metadata-editor">
        <div className="metadata-editor__header">
          <h3 className="metadata-editor__title">{title}</h3>
          {showSearch && (
            <div className="metadata-editor__search">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="metadata-editor__search-input"
              />
            </div>
          )}
        </div>
        <div className="metadata-editor__empty">
          {searchTerm ? 'Nothing found for your query' : 'No data to display'}
        </div>
      </div>
    );
  }

  return (
    <div className="metadata-editor">
      <div className="metadata-editor__header">
        <h3 className="metadata-editor__title">{title}</h3>
        <div className="metadata-editor__actions">
          {showSearch && (
            <div className="metadata-editor__search">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="metadata-editor__search-input"
              />
            </div>
          )}
          {!readOnly && (
            <>
              <button
                onClick={handleSave}
                disabled={loading}
                className={`metadata-editor__btn metadata-editor__btn--save ${loading ? 'loading' : ''}`}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: '6px' }} />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
              <button
                onClick={handleReset}
                className="metadata-editor__btn metadata-editor__btn--reset"
              >
                Reset
              </button>
            </>
          )}
        </div>
      </div>

      <div className="metadata-editor__content" style={{ maxHeight }}>
        <div className="metadata-editor__fields">
          {renderMetadataFields(filteredMetadata, '', handleUpdate, readOnly, 0)}
        </div>
      </div>
    </div>
  );
};

export default MetadataEditor;
